import {ReactNode, useState} from "react";
import {css, cx} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {rpFeatureFlagMultiLeadInsideConversations} from "../../../../../../app/read_rp_environment_variables";
import {IApplication} from "../../../../../../application/types/IApplication";
import {IMultiLeadStepPayload} from "../../../../../../application/types/IMultiLeadStepPayload";
import {ISetPreferencesStepPayload} from "../../../../../../application/types/ISetPreferencesStepPayload";
import {ChatBotConversationItem, IChatBotConversationItemProps} from "../../../../../../atoms/chat_bot/ChatBotConversationItem";
import {ChatBotDotsAnimation} from "../../../../../../atoms/chat_bot/ChatBotDotsAnimation";
import {ScrollToElement} from "../../../../../../atoms/ScrollToElement";
import {useGetOfferDetailApplicationQuery} from "../../../../../../offer/api/get_offer_detail";
import {useAppSelector} from "../../../../../../store/hooks";
import {useInitialLeadFormValues} from "../../../../../hooks/use_initial_lead_form_values";
import {useOpenedLeadModalRequiredParams} from "../../../../../hooks/use_lead_modal_required_params";
import {ILeadModalPriceConversationPrice} from "../../../../../types/ILeadModalPriceConversationPrice";
import {LeadModalPriceConversationPropertyInfo} from "../LeadModalPriceConversationPropertyInfo";
import {LeadModalPriceConversationChatLead} from "./lead_modal_price_conversation_chat_lead/LeadModalPriceConversationChatLead";
import {LeadModalPriceConversationChatRedirect} from "./LeadModalPriceConversationChatRedirect";

type ILeadModalPriceConversationChatChildren =
    | (({
          sentApplication,
          ConversationItem
      }: {
          sentApplication: IApplication | null;
          ConversationItem: (props: Pick<IChatBotConversationItemProps, "initialVisibleMessagesCount" | "messages" | "actions">) => ReactNode;
      }) => ReactNode)
    | null;

interface IProps {
    headerSlot?: ReactNode;
    handleModalClose: (forceClose: boolean) => void;
    handleSetPreferencesStep: (payload: ISetPreferencesStepPayload) => void;
    handleSetPrice: (priceRange: ILeadModalPriceConversationPrice) => void;
    handleSetMultiLeadStep: (payload: IMultiLeadStepPayload) => void;
    handleUpdateMainLeadResponse: (sentApplication: IApplication) => void;
    price: ILeadModalPriceConversationPrice | null;
    showPropertyInfoAfterLead?: boolean;
    className?: string;
    children?: ILeadModalPriceConversationChatChildren;
    hideRodoTerms?: boolean;
}

export function LeadModalPriceConversationChatVariantBCD(props: IProps) {
    const {
        hideRodoTerms,
        children,
        headerSlot,
        price,
        showPropertyInfoAfterLead,
        handleModalClose,
        handleSetPrice,
        handleSetMultiLeadStep,
        handleSetPreferencesStep,
        handleUpdateMainLeadResponse
    } = props;

    const {vendorId, offerId, propertyId} = useOpenedLeadModalRequiredParams("property");
    const isOpened = useAppSelector((state) => state.leadModal.isModalOpen);

    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId);

    const initialLeadFormValues = useInitialLeadFormValues({
        realEstateIds: {
            vendorId,
            offerId,
            propertyId
        },
        leadData: offerDetail
            ? {
                  offer: {
                      detail: offerDetail
                  }
              }
            : null,
        resetValues: !isOpened
    });

    const [sentApplication, setSentApplication] = useState<null | IApplication>(null);

    const handleSuccessfulApplication = async (application: IApplication, price: ILeadModalPriceConversationPrice | null) => {
        handleUpdateMainLeadResponse(application);

        setSentApplication(application);

        if (price) {
            handleSetPrice(price);
        }
    };

    return (
        <div className={cx(conversationStyles, props.className)}>
            {headerSlot}
            <div className={chatStyles}>
                <ChatBotConversationItem
                    className={sentApplication ? rpMessageStyles : undefined}
                    initialVisibleMessagesCount={1}
                    messages={[
                        props.price
                            ? "Uzupełnij swoje dane, aby deweloper mógł przekazać Ci wszystkie szczegóły dotyczące tej nieruchomości."
                            : "Dzień dobry! Cena tego mieszkania jest już dostępna. Aby ją teraz zobaczyć, uzupełnij swoje dane. Dzięki temu deweloper będzie mógł przekazać Ci wszystkie szczegóły dotyczące tej nieruchomości."
                    ]}
                    actions={
                        <ScrollToElement>
                            {initialLeadFormValues && offerDetail && !sentApplication && (
                                <LeadModalPriceConversationChatLead
                                    initialValues={{
                                        name: initialLeadFormValues.name,
                                        email: initialLeadFormValues.email,
                                        phone: {code: initialLeadFormValues.phone.code, number: initialLeadFormValues.phone.number},
                                        marketing_email: initialLeadFormValues.marketing_email,
                                        marketing_sms: initialLeadFormValues.marketing_sms
                                    }}
                                    handleModalClose={handleModalClose}
                                    handleSuccess={handleSuccessfulApplication}
                                    price={props.price}
                                    hideRodoTerms={hideRodoTerms}
                                />
                            )}
                        </ScrollToElement>
                    }
                    messageLoadingRender={
                        <ScrollToElement>
                            <ChatBotDotsAnimation />
                        </ScrollToElement>
                    }
                />
                <LeadModalPriceConversationChatRedirect.PreFetch />
                {sentApplication && (
                    <>
                        <Text variant="info_txt_1" className={userMessageStyles}>
                            {sentApplication.name}
                        </Text>
                        <Text variant="info_txt_1" className={userMessageStyles}>
                            {sentApplication.email}
                        </Text>
                        <Text variant="info_txt_1" className={userMessageStyles}>
                            {sentApplication.phone}
                        </Text>
                        <ChatBotConversationItem
                            messageShowInterval={500}
                            messages={[
                                {
                                    messageContent: "Przekazaliśmy Twoje dane do dewelopera!",
                                    scrollToMessage: true,
                                    className: rpMessageStyles
                                }
                            ]}
                            messageLoadingRender={
                                <ScrollToElement>
                                    <ChatBotDotsAnimation />
                                </ScrollToElement>
                            }
                            actions={
                                <>
                                    {!!showPropertyInfoAfterLead && (
                                        <LeadModalPriceConversationPropertyInfo variant="small" estimatedPrice={price} className={propertyInfoStyles} />
                                    )}
                                    <LeadModalPriceConversationChatRedirect
                                        sentApplication={sentApplication}
                                        handleSetPreferencesStep={handleSetPreferencesStep}
                                        handleSetMultiLeadStep={rpFeatureFlagMultiLeadInsideConversations ? handleSetMultiLeadStep : undefined}
                                        customLoader={<span />}
                                        minLoaderDuration={0}
                                        handleModalClose={handleModalClose}
                                    />
                                </>
                            }
                        />
                    </>
                )}

                {children?.({
                    sentApplication,
                    ConversationItem: (params) => {
                        return (
                            <ChatBotConversationItem
                                initialVisibleMessagesCount={params.initialVisibleMessagesCount}
                                messages={params.messages}
                                messageLoadingRender={
                                    <ScrollToElement>
                                        <ChatBotDotsAnimation />
                                    </ScrollToElement>
                                }
                                actions={params.actions}
                            />
                        );
                    }
                })}
            </div>
        </div>
    );
}

// Styles
const conversationStyles = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto"
});

const chatStyles = css({
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    background: "var(--colors-gray-300)",
    padding: 2,
    overflowY: "scroll"
});

const rpMessageStyles = css({
    p: 0,
    pr: 4
});

const userMessageStyles = css({
    background: "var(--colors-info)",
    borderRadius: 2,
    padding: 1,
    alignSelf: "flex-end",
    mb: 1
});

const propertyInfoStyles = css({
    borderRadius: 1,
    background: "#fff",
    py: 2,
    px: 0.5,
    mb: 1,
    width: "100%"
});
import {ReactNode, useState} from "react";
import {css, cx} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {rpFeatureFlagMultiLeadInsideConversations} from "../../../../../../app/read_rp_environment_variables";
import {IApplication} from "../../../../../../application/types/IApplication";
import {IMultiLeadStepPayload} from "../../../../../../application/types/IMultiLeadStepPayload";
import {ISetPreferencesStepPayload} from "../../../../../../application/types/ISetPreferencesStepPayload";
import {ChatBotConversationItem, IChatBotConversationItemProps} from "../../../../../../atoms/chat_bot/ChatBotConversationItem";
import {ChatBotDotsAnimation} from "../../../../../../atoms/chat_bot/ChatBotDotsAnimation";
import {ScrollToElement} from "../../../../../../atoms/ScrollToElement";
import {useGetOfferDetailApplicationQuery} from "../../../../../../offer/api/get_offer_detail";
import {useAppSelector} from "../../../../../../store/hooks";
import {useInitialLeadFormValues} from "../../../../../hooks/use_initial_lead_form_values";
import {useOpenedLeadModalRequiredParams} from "../../../../../hooks/use_lead_modal_required_params";
import {ILeadModalPriceConversationPrice} from "../../../../../types/ILeadModalPriceConversationPrice";
import {LeadModalPriceConversationPropertyInfo} from "../LeadModalPriceConversationPropertyInfo";
import {LeadModalPriceConversationChatLead} from "./lead_modal_price_conversation_chat_lead/LeadModalPriceConversationChatLead";
import {LeadModalPriceConversationChatRedirect} from "./LeadModalPriceConversationChatRedirect";

type ILeadModalPriceConversationChatChildren =
    | (({
          sentApplication,
          ConversationItem
      }: {
          sentApplication: IApplication | null;
          ConversationItem: (props: Pick<IChatBotConversationItemProps, "initialVisibleMessagesCount" | "messages" | "actions">) => ReactNode;
      }) => ReactNode)
    | null;

interface IProps {
    headerSlot?: ReactNode;
    handleModalClose: (forceClose: boolean) => void;
    handleSetPreferencesStep: (payload: ISetPreferencesStepPayload) => void;
    handleSetPrice: (priceRange: ILeadModalPriceConversationPrice) => void;
    handleSetMultiLeadStep: (payload: IMultiLeadStepPayload) => void;
    handleUpdateMainLeadResponse: (sentApplication: IApplication) => void;
    price: ILeadModalPriceConversationPrice | null;
    showPropertyInfoAfterLead?: boolean;
    className?: string;
    children?: ILeadModalPriceConversationChatChildren;
    hideRodoTerms?: boolean;
}

export function LeadModalPriceConversationChatVariantBCD(props: IProps) {
    const {
        hideRodoTerms,
        children,
        headerSlot,
        price,
        showPropertyInfoAfterLead,
        handleModalClose,
        handleSetPrice,
        handleSetMultiLeadStep,
        handleSetPreferencesStep,
        handleUpdateMainLeadResponse
    } = props;

    const {vendorId, offerId, propertyId} = useOpenedLeadModalRequiredParams("property");
    const isOpened = useAppSelector((state) => state.leadModal.isModalOpen);

    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId);

    const initialLeadFormValues = useInitialLeadFormValues({
        realEstateIds: {
            vendorId,
            offerId,
            propertyId
        },
        leadData: offerDetail
            ? {
                  offer: {
                      detail: offerDetail
                  }
              }
            : null,
        resetValues: !isOpened
    });

    const [sentApplication, setSentApplication] = useState<null | IApplication>(null);

    const handleSuccessfulApplication = async (application: IApplication, price: ILeadModalPriceConversationPrice | null) => {
        handleUpdateMainLeadResponse(application);

        setSentApplication(application);

        if (price) {
            handleSetPrice(price);
        }
    };

    return (
        <div className={cx(conversationStyles, props.className)}>
            {headerSlot}
            <div className={chatStyles}>
                <ChatBotConversationItem
                    className={sentApplication ? rpMessageStyles : undefined}
                    initialVisibleMessagesCount={1}
                    messages={[
                        props.price
                            ? "Uzupełnij swoje dane, aby deweloper mógł przekazać Ci wszystkie szczegóły dotyczące tej nieruchomości."
                            : "Dzień dobry! Cena tego mieszkania jest już dostępna. Aby ją teraz zobaczyć, uzupełnij swoje dane. Dzięki temu deweloper będzie mógł przekazać Ci wszystkie szczegóły dotyczące tej nieruchomości."
                    ]}
                    actions={
                        <ScrollToElement>
                            {initialLeadFormValues && offerDetail && !sentApplication && (
                                <LeadModalPriceConversationChatLead
                                    initialValues={{
                                        name: initialLeadFormValues.name,
                                        email: initialLeadFormValues.email,
                                        phone: {code: initialLeadFormValues.phone.code, number: initialLeadFormValues.phone.number},
                                        marketing_email: initialLeadFormValues.marketing_email,
                                        marketing_sms: initialLeadFormValues.marketing_sms
                                    }}
                                    handleModalClose={handleModalClose}
                                    handleSuccess={handleSuccessfulApplication}
                                    price={props.price}
                                    hideRodoTerms={hideRodoTerms}
                                />
                            )}
                        </ScrollToElement>
                    }
                    messageLoadingRender={
                        <ScrollToElement>
                            <ChatBotDotsAnimation />
                        </ScrollToElement>
                    }
                />
                <LeadModalPriceConversationChatRedirect.PreFetch />
                {sentApplication && (
                    <>
                        <Text variant="info_txt_1" className={userMessageStyles}>
                            {sentApplication.name}
                        </Text>
                        <Text variant="info_txt_1" className={userMessageStyles}>
                            {sentApplication.email}
                        </Text>
                        <Text variant="info_txt_1" className={userMessageStyles}>
                            {sentApplication.phone}
                        </Text>
                        <ChatBotConversationItem
                            messageShowInterval={500}
                            messages={[
                                {
                                    messageContent: "Przekazaliśmy Twoje dane do dewelopera!",
                                    scrollToMessage: true,
                                    className: rpMessageStyles
                                }
                            ]}
                            messageLoadingRender={
                                <ScrollToElement>
                                    <ChatBotDotsAnimation />
                                </ScrollToElement>
                            }
                            actions={
                                <>
                                    {!!showPropertyInfoAfterLead && (
                                        <LeadModalPriceConversationPropertyInfo variant="small" estimatedPrice={price} className={propertyInfoStyles} />
                                    )}
                                    <LeadModalPriceConversationChatRedirect
                                        sentApplication={sentApplication}
                                        handleSetPreferencesStep={handleSetPreferencesStep}
                                        handleSetMultiLeadStep={rpFeatureFlagMultiLeadInsideConversations ? handleSetMultiLeadStep : undefined}
                                        customLoader={<span />}
                                        minLoaderDuration={0}
                                        handleModalClose={handleModalClose}
                                    />
                                </>
                            }
                        />
                    </>
                )}

                {children?.({
                    sentApplication,
                    ConversationItem: (params) => {
                        return (
                            <ChatBotConversationItem
                                initialVisibleMessagesCount={params.initialVisibleMessagesCount}
                                messages={params.messages}
                                messageLoadingRender={
                                    <ScrollToElement>
                                        <ChatBotDotsAnimation />
                                    </ScrollToElement>
                                }
                                actions={params.actions}
                            />
                        );
                    }
                })}
            </div>
        </div>
    );
}

// Styles
const conversationStyles = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto"
});

const chatStyles = css({
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    background: "var(--colors-gray-300)",
    padding: 2,
    overflowY: "scroll"
});

const rpMessageStyles = css({
    p: 0,
    pr: 4
});

const userMessageStyles = css({
    background: "var(--colors-info)",
    borderRadius: 2,
    padding: 1,
    alignSelf: "flex-end",
    mb: 1
});

const propertyInfoStyles = css({
    borderRadius: 1,
    background: "#fff",
    py: 2,
    px: 0.5,
    mb: 1,
    width: "100%"
});
